import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, ArrowDownCircle, ArrowUpCircle, BellRing, ChevronRight, Clock3,
  Coins, Dices, Gamepad2, Gauge, LayoutDashboard, Radio, ReceiptText,
  RefreshCw, Server, ShieldCheck, Sparkles, Ticket, TicketPercent,
  TrendingUp, UserRoundPlus, Users, Wallet,
} from 'lucide-react';
import client from '../lib/api';
import { AdminError, AdminSkeleton } from './components/PageState';
import { useAdminPermissions } from './lib/permissions';
import { formatDateTime, formatMoney, formatNumber } from './lib/formatters';
import './dashboard.css';

// Finans akışının kendi kendini tazeleme aralığı. Ağır `/admin/stats`
// toplamları bu turda **çalıştırılmaz**: bekleyen yatırım/çekim hafif uçtan
// okunur, dönem toplamları yalnızca yönetici Yenile'ye bastığında hesaplanır.
export const FINANCE_REFRESH_MS = 15000;

// Kartta çizilen en yeni hesap sayısı. Uç altı satır döndürür; liste yanındaki
// Sistem Durumu kartıyla aynı yükseklikte kalsın diye beşi çizilir — fazlası
// "Tümünü gör" bağlantısının işidir.
export const RECENT_USER_LIMIT = 5;

// Dönem seçimi yöneticinin kendi tarayıcısında durur: sunucuya yazılsaydı bir
// yöneticinin seçimi diğerininkini değiştirirdi ve her geçiş bir istek daha
// olurdu (Herkül'ün sohbet rayı ve Vega'nın görünüm anahtarıyla aynı yaklaşım).
export const PERIOD_STORAGE_KEY = 'admin:dash-period';

export const PERIODS = [
  { id: 'today', label: 'Bugün', short: 'bugün' },
  { id: 'week', label: 'Bu hafta', short: 'bu hafta' },
  { id: 'month', label: 'Bu ay', short: 'bu ay' },
  { id: 'all', label: 'Tüm zamanlar', short: 'tüm zamanlar' },
];

const PERIOD_IDS = PERIODS.map((period) => period.id);

/** Kayıtlı dönem tercihi; tanınmayan ya da okunamayan değer "bugün"e düşer. */
const readStoredPeriod = () => {
  try {
    const stored = window.localStorage.getItem(PERIOD_STORAGE_KEY);
    return PERIOD_IDS.includes(stored) ? stored : 'today';
  } catch (_) {
    // Gizli sekmede ve depolama kapalı tarayıcıda okuma hata fırlatır;
    // dashboard bunun yüzünden açılmamazlık etmemelidir.
    return 'today';
  }
};

const storePeriod = (value) => {
  try {
    window.localStorage.setItem(PERIOD_STORAGE_KEY, value);
  } catch (_) {
    // Tercihin saklanamaması sayfayı bozmaz; seçim o oturum için geçerlidir.
  }
};

/**
 * Bir dönemin rakamları.
 *
 * "Tüm zamanlar" penceresi `periods` içinde yoktur: uç onu kendi alanlarında
 * (`total_deposit`/`total_withdraw`) döndürür. Hepsi aynı ledger sorgusundan,
 * yalnızca onaylı hareketlerden gelir — bekleyen ve reddedilen yatırım/çekim
 * sayılmaz. Toplamı burada dönemleri toplayarak üretmeyin.
 */
export const periodValues = (stats, periodId) => {
  if (periodId === 'all') {
    return {
      users: Number(stats.total_users || 0),
      deposit: Number(stats.total_deposit || 0),
      withdraw: Number(stats.total_withdraw || 0),
      net_income: Number(stats.total_net_income || 0),
      ggr: Number(stats.total_ggr || 0),
    };
  }
  const row = stats.periods?.[periodId] || {};
  return {
    users: Number(row.users || 0),
    deposit: Number(row.deposit || 0),
    withdraw: Number(row.withdraw || 0),
    net_income: Number(row.net_income || 0),
    ggr: Number(row.ggr || 0),
  };
};

/**
 * Büyük tutarların kısaltılmış gösterimi (12,4 Mn ₺).
 *
 * KPI kartı sabit genişliktedir ve sekiz haneli bir tutar tam yazımla satıra
 * sığmayıp ortadan kırılıyordu. Kısaltma yalnızca **büyük** değerlere
 * uygulanır; günlük tutarlar tam yazılmalıdır, "1,2 B ₺" ile "1.234,50 ₺"
 * aynı şey değildir. Tam değer her zaman `title` olarak taşınır.
 */
const COMPACT_MONEY_THRESHOLD = 1000000;

export const compactMoney = (value) => {
  const amount = Number(value || 0);
  if (Math.abs(amount) < COMPACT_MONEY_THRESHOLD) return formatMoney(amount);
  return formatMoney(amount, {
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
};

/** Dönemin toplam içindeki payı (0-100). Toplam yoksa çubuk çizilmez. */
export const shareOf = (value, total) => {
  const base = Math.abs(Number(total || 0));
  if (!base) return 0;
  return Math.min(100, Math.round((Math.abs(Number(value || 0)) / base) * 100));
};

const formatClock = (value) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
};

const STATUS_LABELS = {
  beklemede: 'Onay bekliyor',
  onaylandi: 'Onaylandı',
  reddedildi: 'Reddedildi',
};

const STATUS_BADGES = {
  beklemede: 'badge-yellow',
  onaylandi: 'badge-green',
  reddedildi: 'badge-red',
};

// Hızlı erişim kısayolları. İzin anahtarları `lib/navigation.js` içindeki menü
// haritasıyla **aynı kalmalıdır**: yetkisi olmayan yöneticiye kısayol
// göstermek onu yalnızca 403 ekranına götürür.
const QUICK_LINKS = [
  {
    to: '/admin/users',
    label: 'Kullanıcılar',
    text: 'Hesap, bakiye ve kısıt yönetimi',
    icon: Users,
    tone: '56, 189, 248',
    permission: 'users.read',
  },
  {
    to: '/admin/finance',
    label: 'Finans İşlemleri',
    text: 'Yatırım ve çekim kararları',
    icon: Coins,
    tone: '32, 217, 120',
    permission: 'finance.read',
  },
  {
    to: '/admin/kyc',
    label: 'KYC Onayları',
    text: 'Kimlik doğrulama kuyruğu',
    icon: ShieldCheck,
    tone: '167, 139, 250',
    permission: 'users.write',
  },
  {
    to: '/admin/sports-bets',
    label: 'Spor Bahisleri',
    text: 'Kuponlar ve sonuçlandırma',
    icon: Ticket,
    tone: '244, 114, 182',
    permission: 'finance.read',
  },
  {
    to: '/admin/promo-codes',
    label: 'Promo Kodları',
    text: 'Nakit ve freespin kodları',
    icon: TicketPercent,
    tone: '139, 92, 246',
    permission: 'promo.read',
  },
  {
    to: '/admin/games',
    label: 'Oyun Yönetimi',
    text: 'Vitrin, katalog ve sağlayıcılar',
    icon: Gamepad2,
    tone: '245, 158, 11',
    permission: 'games.read',
  },
];

const Dashboard = () => {
  const { can } = useAdminPermissions();
  const [stats, setStats] = useState(null);
  const [financeActivity, setFinanceActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState(readStoredPeriod);
  const [syncedAt, setSyncedAt] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    setError('');
    try {
      const [statsResponse, financeResponse] = await Promise.all([
        client.get('/admin/stats'),
        client.get('/admin/finance-notifications'),
      ]);
      setStats(statsResponse.data);
      setFinanceActivity(financeResponse.data);
      setSyncedAt(new Date());
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Dashboard verileri alınamadı');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const refreshFinanceActivity = async () => {
      try {
        const { data } = await client.get('/admin/finance-notifications');
        setFinanceActivity(data);
        setSyncedAt(new Date());
      } catch (_) {
        // Arka plan yenilemesi mevcut dashboard'u hata ekranına dönüştürmez.
      }
    };
    // Yenileme yalnızca sekme görünürken döner: arka planda unutulan bir
    // panel, sağlayıcı webhook'larıyla aynı tek worker'ı boşuna yormamalıdır
    // (Spor Bahisleri sayfasındaki canlı turun kuralıyla aynı).
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      refreshFinanceActivity();
    };
    const timer = window.setInterval(tick, FINANCE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  const selectPeriod = useCallback((value) => {
    setPeriod(value);
    storePeriod(value);
  }, []);

  const canFinance = can('finance.read');
  const canUsers = can('users.read');
  const canSettings = can('content.read');

  const quickLinks = useMemo(
    () => QUICK_LINKS.filter((item) => can(item.permission)),
    [can],
  );

  const activePeriod = PERIODS.find((item) => item.id === period) || PERIODS[0];
  const current = stats ? periodValues(stats, period) : null;
  const overall = stats ? periodValues(stats, 'all') : null;

  // Dönem karşılaştırması hep üç pencereyi birlikte gösterir: seçili dönem
  // tek başına "çok mu az mı" sorusunu yanıtlamıyordu.
  const trends = useMemo(() => {
    if (!stats) return [];
    const rows = PERIODS.filter((item) => item.id !== 'all').map((item) => ({
      id: item.id,
      label: item.label,
      ...periodValues(stats, item.id),
    }));
    const peak = rows.reduce(
      (highest, row) => Math.max(highest, row.deposit, row.withdraw),
      0,
    );
    return rows.map((row) => ({
      ...row,
      depositWidth: peak ? (row.deposit / peak) * 100 : 0,
      withdrawWidth: peak ? (row.withdraw / peak) * 100 : 0,
    }));
  }, [stats]);

  if (loading) {
    return <AdminSkeleton rows={7} label="Dashboard verileri hazırlanıyor" />;
  }

  if (error || !stats) {
    return <AdminError message={error || 'Dashboard verisi bulunamadı'} onRetry={load} />;
  }

  const onlineUsers = Number(financeActivity?.online_users ?? stats.online_users ?? 0);
  const activeUsers = Number(stats.active_users || 0);
  const totalUsers = Number(stats.total_users || 0);
  const bannedUsers = Number(stats.banned_users || 0);
  const todayRegistered = Number(stats.today_registered || 0);
  const onlineRatio = activeUsers ? Math.min(100, Math.round((onlineUsers / activeUsers) * 100)) : 0;

  const recentUsers = (stats.recent_users || []).slice(0, RECENT_USER_LIMIT);

  const pendingCount = Number(financeActivity?.pending_count || 0);
  const hasPending = pendingCount > 0;
  const financeItems = financeActivity?.items || [];

  const kpis = [
    {
      key: 'deposit',
      label: 'Yatırım',
      icon: ArrowDownCircle,
      tone: '32, 217, 120',
      value: current.deposit,
      total: overall.deposit,
      hint: 'Onaylanmış para girişi',
    },
    {
      key: 'withdraw',
      label: 'Çekim',
      icon: ArrowUpCircle,
      tone: '240, 68, 90',
      value: current.withdraw,
      total: overall.withdraw,
      hint: 'Onaylanmış para çıkışı',
    },
    {
      // Kasaya kalan para: yatırım − çekim. Oyun tarafının brüt geliri (GGR)
      // bonusla oynanan turları da saydığı için yatırımın kat kat üstüne
      // çıkabiliyor ve "net gelir" olarak okunduğunda yanıltıyordu; iki rakam
      // bu yüzden ayrı kartlardadır.
      key: 'net_income',
      label: 'Net Kasa',
      icon: Wallet,
      tone: '245, 158, 11',
      value: current.net_income,
      total: overall.net_income,
      hint: 'Yatırım eksi çekim',
      signed: true,
    },
    {
      key: 'ggr',
      label: 'Oyun Geliri',
      icon: Dices,
      tone: '139, 92, 246',
      value: current.ggr,
      total: overall.ggr,
      hint: 'Bahis eksi kazanç (GGR)',
      signed: true,
    },
    {
      key: 'users',
      label: period === 'all' ? 'Toplam Üye' : 'Yeni Üye',
      icon: UserRoundPlus,
      tone: '56, 189, 248',
      value: current.users,
      total: overall.users,
      hint: period === 'all' ? 'Kayıtlı tüm hesaplar' : 'Dönemde açılan hesap',
      count: true,
    },
  ];

  return (
    <div className="admin-page dash-page">
      <header className="dash-hero">
        <span className="dash-hero-mark"><LayoutDashboard size={23} /></span>
        <div className="dash-hero-copy">
          <h1>Operasyon Dashboard’u</h1>
          <p>
            Finans, üye ve oyun verilerinin canlı özeti. Bekleyen yatırım ve
            çekimler {FINANCE_REFRESH_MS / 1000} saniyede bir kendiliğinden
            tazelenir; dönem toplamları yalnızca yenilendiğinde hesaplanır.
          </p>
        </div>
        <div className="dash-hero-actions">
          <span className="dash-stamp" title="Finans akışının son okunma saati">
            <span className="dash-stamp-dot" aria-hidden="true" />
            Son eşitleme {formatClock(syncedAt)}
          </span>
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={load}
            disabled={refreshing}
          >
            <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} aria-hidden="true" />
            Yenile
          </button>
        </div>
      </header>

      <div className="dash-periodbar">
        <span className="dash-periodbar-label">
          <Clock3 size={13} aria-hidden="true" /> Dönem
        </span>
        <div className="dash-seg" role="group" aria-label="Dönem süzgeci">
          {PERIODS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={item.id === period ? 'is-on' : ''}
              aria-pressed={item.id === period}
              onClick={() => selectPeriod(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <span className="dash-periodbar-note">
          Kartlar {activePeriod.short} penceresini gösterir · gün sınırı Türkiye saatidir
        </span>
      </div>

      <section className="dash-kpis" aria-label="Dönem özeti">
        {kpis.map((kpi) => {
          const share = kpi.total ? shareOf(kpi.value, kpi.total) : 0;
          const negative = kpi.signed && Number(kpi.value) < 0;
          const display = kpi.count
            ? formatNumber(kpi.value)
            : compactMoney(kpi.value);
          const exact = kpi.count ? formatNumber(kpi.value) : formatMoney(kpi.value);
          return (
            <article
              key={kpi.key}
              className="dash-kpi"
              style={{ '--dash-tone-rgb': kpi.tone, '--dash-tone': `rgb(${kpi.tone})` }}
            >
              <div className="dash-kpi-top">
                <span className="dash-kpi-icon"><kpi.icon size={18} aria-hidden="true" /></span>
                <span className="dash-kpi-name">{kpi.label}</span>
                {kpi.signed ? (
                  <span className={`dash-kpi-chip ${negative ? 'is-down' : 'is-up'}`}>
                    {negative ? 'Eksi' : 'Artı'}
                  </span>
                ) : (
                  <span className="dash-kpi-chip">{activePeriod.label}</span>
                )}
              </div>

              <div>
                <div className="dash-kpi-value" title={exact}>{display}</div>
                <div className="dash-kpi-sub">{kpi.hint}</div>
              </div>

              <div className="dash-kpi-foot">
                <div className="dash-kpi-footrow">
                  <span>{period === 'all' ? 'Toplam' : 'Tüm zamanlar'}</span>
                  <strong title={kpi.count ? formatNumber(kpi.total) : formatMoney(kpi.total)}>
                    {kpi.count ? formatNumber(kpi.total) : compactMoney(kpi.total)}
                  </strong>
                </div>
                {period !== 'all' && (
                  <div
                    className="dash-kpi-bar"
                    role="img"
                    aria-label={`Toplamın yüzde ${share} kadarı`}
                  >
                    <span style={{ width: `${share}%` }} />
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </section>

      <div className="dash-row is-split">
        <section className="dash-card" aria-label="Üye durumu">
          <div className="dash-card-head">
            <span className="dash-card-mark"><Radio size={17} aria-hidden="true" /></span>
            <div className="dash-card-titles">
              <h2>Üye Durumu</h2>
              <p>Şu anda sitede olan hesaplar ve üye dağılımı</p>
            </div>
            {canUsers && (
              <Link to="/admin/users" className="dash-card-link">
                Kullanıcılar <ChevronRight size={13} aria-hidden="true" />
              </Link>
            )}
          </div>
          <div className="dash-card-body">
            <div className="dash-gauge-wrap">
              <div
                className="dash-gauge"
                style={{ '--dash-ratio': onlineRatio }}
                role="img"
                aria-label={`Aktif hesapların yüzde ${onlineRatio} kadarı çevrimiçi`}
              >
                <span className="dash-gauge-inner">
                  <strong>{formatNumber(onlineUsers)}</strong>
                  <span>Çevrimiçi</span>
                </span>
              </div>
              <div className="dash-facts">
                <div className="dash-fact" style={{ '--dash-fact-tone': '#22d3ee' }}>
                  <span className="dash-fact-dot" aria-hidden="true" />
                  <span className="dash-fact-label">Aktif hesapların oranı</span>
                  <span className="dash-fact-value">%{onlineRatio}</span>
                </div>
                <div className="dash-fact" style={{ '--dash-fact-tone': '#20d978' }}>
                  <span className="dash-fact-dot" aria-hidden="true" />
                  <span className="dash-fact-label">Aktif hesap</span>
                  <span className="dash-fact-value">{formatNumber(activeUsers)}</span>
                </div>
                <div className="dash-fact" style={{ '--dash-fact-tone': '#38bdf8' }}>
                  <span className="dash-fact-dot" aria-hidden="true" />
                  <span className="dash-fact-label">Bugün kayıt olan</span>
                  <span className="dash-fact-value">{formatNumber(todayRegistered)}</span>
                </div>
                <div className="dash-fact" style={{ '--dash-fact-tone': '#f0445a' }}>
                  <span className="dash-fact-dot" aria-hidden="true" />
                  <span className="dash-fact-label">Yasaklı hesap</span>
                  <span className="dash-fact-value">{formatNumber(bannedUsers)}</span>
                </div>
                <div className="dash-fact" style={{ '--dash-fact-tone': '#94a3b8' }}>
                  <span className="dash-fact-dot" aria-hidden="true" />
                  <span className="dash-fact-label">Toplam üye</span>
                  <span className="dash-fact-value">{formatNumber(totalUsers)}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="dash-card" aria-label="Dönem karşılaştırması">
          <div className="dash-card-head">
            <span className="dash-card-mark"><TrendingUp size={17} aria-hidden="true" /></span>
            <div className="dash-card-titles">
              <h2>Para Akışı</h2>
              <p>Üç pencerede yatırım ve çekim; çubuklar en yüksek döneme göre ölçeklenir</p>
            </div>
          </div>
          <div className="dash-card-body">
            <div className="dash-trends">
              {trends.map((row) => {
                const net = Number(row.net_income || 0);
                return (
                  <div className="dash-trend" key={row.id}>
                    <div className="dash-trend-head">
                      <span className="dash-trend-name">{row.label}</span>
                      <span className={`dash-trend-net ${net < 0 ? 'is-minus' : 'is-plus'}`}>
                        {net < 0 ? '' : '+'}{compactMoney(net)}
                      </span>
                    </div>
                    <div className="dash-trend-track" aria-hidden="true">
                      <i className="is-in" style={{ width: `${row.depositWidth / 2}%` }} />
                      <i className="is-out" style={{ width: `${row.withdrawWidth / 2}%` }} />
                    </div>
                    <div className="dash-trend-legend">
                      <span className="dash-legendkey" style={{ '--dash-key': '#20d978' }}>
                        Yatırım <b>{compactMoney(row.deposit)}</b>
                      </span>
                      <span className="dash-legendkey" style={{ '--dash-key': '#f0445a' }}>
                        Çekim <b>{compactMoney(row.withdraw)}</b>
                      </span>
                      <span className="dash-legendkey" style={{ '--dash-key': '#8b5cf6' }}>
                        Oyun geliri <b>{compactMoney(row.ggr)}</b>
                      </span>
                    </div>
                  </div>
                );
              })}
              {trends.length === 0 && (
                <p className="dash-empty">Dönem verisi bulunamadı</p>
              )}
            </div>
          </div>
        </section>
      </div>

      <section className={`dash-card dash-finance ${hasPending ? 'is-alert' : ''}`} aria-label="Finans bildirimleri">
        <div className="dash-card-head">
          <span className={`dash-card-mark ${hasPending ? 'is-warn' : ''}`}>
            <BellRing size={17} aria-hidden="true" />
          </span>
          <div className="dash-card-titles">
            <h2>
              Finans Bildirim Merkezi
              <span className={`badge-pill ${hasPending ? 'badge-yellow' : 'badge-gray'}`}>
                {hasPending ? `${formatNumber(pendingCount)} bekleyen` : 'Bekleyen yok'}
              </span>
            </h2>
            <p>Yeni yatırım ve çekimler otomatik kontrol edilir; karar Finans sayfasında verilir</p>
          </div>
          {canFinance && (
            <Link to="/admin/finance?status=beklemede" className="dash-card-link">
              Bekleyenleri aç <ChevronRight size={13} aria-hidden="true" />
            </Link>
          )}
        </div>

        <div className="dash-card-body">
          <div className="dash-pending">
            <div className={`dash-pending-box ${hasPending ? 'is-alert' : ''}`}>
              <span><Clock3 size={11} aria-hidden="true" /> Bekleyen toplam</span>
              <strong>{formatNumber(pendingCount)} işlem</strong>
              <small title={formatMoney(financeActivity?.pending_amount)}>
                {compactMoney(financeActivity?.pending_amount)} tutarında
              </small>
            </div>
            <div className="dash-pending-box">
              <span><ArrowDownCircle size={11} aria-hidden="true" /> Bekleyen yatırım</span>
              <strong>{formatNumber(financeActivity?.pending_deposit_count)} işlem</strong>
              <small title={formatMoney(financeActivity?.pending_deposit_amount)}>
                {compactMoney(financeActivity?.pending_deposit_amount)}
              </small>
            </div>
            <div className="dash-pending-box">
              <span><ArrowUpCircle size={11} aria-hidden="true" /> Bekleyen çekim</span>
              <strong>{formatNumber(financeActivity?.pending_withdraw_count)} işlem</strong>
              <small title={formatMoney(financeActivity?.pending_withdraw_amount)}>
                {compactMoney(financeActivity?.pending_withdraw_amount)}
              </small>
            </div>
            <div className="dash-pending-box">
              <span><Activity size={11} aria-hidden="true" /> Bugünkü hareket</span>
              <strong>{formatNumber(financeActivity?.today_count)} işlem</strong>
              <small>
                {financeActivity?.latest_pending_at
                  ? `Son bekleyen ${formatDateTime(financeActivity.latest_pending_at)}`
                  : 'Bekleyen talep yok'}
              </small>
            </div>
          </div>
        </div>

        <div className="dash-feed">
          {financeItems.map((item) => {
            const isWithdraw = item.type === 'withdraw';
            const ActivityIcon = isWithdraw ? ArrowUpCircle : ArrowDownCircle;
            const tone = isWithdraw ? '#f0445a' : '#20d978';
            const body = (
              <>
                <div className="dash-feed-top">
                  <span className="dash-feed-icon"><ActivityIcon size={16} aria-hidden="true" /></span>
                  <span className="dash-feed-who">
                    <strong>{item.username}</strong>
                    <span>{isWithdraw ? 'Çekim' : 'Yatırım'} · {item.method}</span>
                  </span>
                  <span className="dash-feed-amount">
                    <b>{isWithdraw ? '−' : '+'}{formatMoney(item.amount)}</b>
                    <i>{formatDateTime(item.created_at)}</i>
                  </span>
                </div>
                <div className="dash-feed-foot">
                  <span className={`badge-pill ${STATUS_BADGES[item.status] || 'badge-gray'}`}>
                    {STATUS_LABELS[item.status] || item.status}
                  </span>
                  {!isWithdraw && item.has_receipt && (
                    <span className="dash-feed-hint">
                      <ReceiptText size={11} aria-hidden="true" /> Dekont var
                    </span>
                  )}
                  {canFinance && (
                    <span className="dash-feed-go">
                      <ChevronRight size={14} aria-hidden="true" />
                    </span>
                  )}
                </div>
              </>
            );
            // Finans iznine sahip olmayan yönetici (ör. bonus operatörü)
            // dashboard'u görebilir ama listeye tıklayamaz: bağlantı yalnızca
            // 403 ekranına götürürdü.
            return canFinance ? (
              <Link
                key={item.id}
                to={`/admin/finance?type=${isWithdraw ? 'withdraw' : 'deposit'}`}
                className="dash-feed-item"
                style={{ '--dash-feed-tone': tone }}
              >
                {body}
              </Link>
            ) : (
              <div key={item.id} className="dash-feed-item" style={{ '--dash-feed-tone': tone }}>
                {body}
              </div>
            );
          })}
          {financeItems.length === 0 && (
            <p className="dash-empty">Henüz yatırım veya çekim hareketi bulunmuyor</p>
          )}
        </div>
      </section>

      <div className="dash-row is-wide">
        <section className="dash-card" aria-label="Son kayıt olan kullanıcılar">
          <div className="dash-card-head">
            <span className="dash-card-mark"><Sparkles size={17} aria-hidden="true" /></span>
            <div className="dash-card-titles">
              <h2>Son Kayıt Olan Üyeler</h2>
              <p>En yeni hesaplar, bakiyeleri ve doğrulama durumları</p>
            </div>
            {canUsers && (
              <Link to="/admin/users" className="dash-card-link">
                Tümünü gör <ChevronRight size={13} aria-hidden="true" />
              </Link>
            )}
          </div>
          <div className="dash-card-body is-flush">
            <div className="dash-people">
              {recentUsers.map((user) => {
                const initials = (user.username || '?').slice(0, 2);
                const row = (
                  <>
                    <span className="dash-avatar" aria-hidden="true">{initials}</span>
                    <span className="dash-person-main">
                      <strong>{user.username}</strong>
                      <span>{formatDateTime(user.created_at)}</span>
                    </span>
                    <span className="dash-person-money" title={formatMoney(user.balance)}>
                      {compactMoney(user.balance)}
                    </span>
                    <span className="dash-person-tags">
                      <span className={`badge-pill ${user.kyc_verified ? 'badge-green' : 'badge-yellow'}`}>
                        <ShieldCheck size={11} aria-hidden="true" />
                        {user.kyc_verified ? 'Doğrulandı' : 'KYC bekliyor'}
                      </span>
                      <span className={`badge-pill ${user.is_active ? 'badge-green' : 'badge-red'}`}>
                        {user.is_active ? 'Aktif' : 'Kısıtlı'}
                      </span>
                    </span>
                  </>
                );
                return canUsers ? (
                  <Link key={user.id} to={`/admin/users/${user.id}`} className="dash-person">
                    {row}
                  </Link>
                ) : (
                  <div key={user.id} className="dash-person">{row}</div>
                );
              })}
              {recentUsers.length === 0 && (
                <p className="dash-empty">Henüz kayıtlı üye yok</p>
              )}
            </div>
          </div>
        </section>

        <section className="dash-card" aria-label="Sistem durumu">
          <div className="dash-card-head">
            <span className="dash-card-mark"><Server size={17} aria-hidden="true" /></span>
            <div className="dash-card-titles">
              <h2>Sistem Durumu</h2>
              <p>Yayındaki oyun ve kayıt ayarları</p>
            </div>
            {canSettings && (
              <Link to="/admin/site-ayarlari" className="dash-card-link">
                Ayarlar <ChevronRight size={13} aria-hidden="true" />
              </Link>
            )}
          </div>
          <div className="dash-card-body">
            <div className="dash-status">
              <StatusRow
                label="Slot sağlayıcı"
                value={stats.configuration?.active_slot_provider || 'Aktif sağlayıcı yok'}
                active={Boolean(stats.configuration?.active_slot_provider)}
              />
              <StatusRow
                label="Aktif sağlayıcı sayısı"
                value={formatNumber(stats.configuration?.active_game_providers)}
                active={Number(stats.configuration?.active_game_providers) > 0}
              />
              <StatusRow
                label="Demo modu"
                value={stats.configuration?.demo_enabled ? 'Açık' : 'Kapalı'}
                active={Boolean(stats.configuration?.demo_enabled)}
              />
              <StatusRow
                label="Yeni kayıt"
                value={stats.configuration?.registration_open ? 'Açık' : 'Kapalı'}
                active={Boolean(stats.configuration?.registration_open)}
              />
            </div>
          </div>
        </section>
      </div>

      <section className="dash-card" aria-label="Toplam rakamlar">
        <div className="dash-card-head">
          <span className="dash-card-mark"><Gauge size={17} aria-hidden="true" /></span>
          <div className="dash-card-titles">
            <h2>Toplam Rakamlar</h2>
            <p>Dönemden bağımsız, kuruluşun açılışından bugüne biriken değerler</p>
          </div>
        </div>
        <div className="dash-card-body">
          <div className="dash-mini">
            <MiniBox
              icon={Wallet}
              tone="#22d3ee"
              // Üye cüzdanlarında duran para: kasanın geliri değil yükümlülüğü.
              label="Üye bakiyeleri"
              value={compactMoney(stats.total_balance)}
              title={formatMoney(stats.total_balance)}
            />
            <MiniBox
              icon={Dices}
              tone="#8b5cf6"
              label="Toplam oyun geliri"
              value={compactMoney(stats.total_ggr)}
              title={formatMoney(stats.total_ggr)}
            />
            <MiniBox
              icon={Coins}
              tone="#20d978"
              label="Finans hareketi"
              value={formatNumber(stats.total_transactions)}
            />
            <MiniBox
              icon={Ticket}
              tone="#f472b6"
              label="Spor kuponu"
              value={formatNumber(stats.total_coupons)}
            />
            <MiniBox
              icon={Users}
              tone="#38bdf8"
              label="Kayıtlı üye"
              value={formatNumber(totalUsers)}
            />
          </div>
        </div>
      </section>

      {quickLinks.length > 0 && (
        <section className="dash-links" aria-label="Hızlı erişim">
          {quickLinks.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="dash-link"
              style={{ '--dash-tone-rgb': item.tone, '--dash-tone': `rgb(${item.tone})` }}
            >
              <span className="dash-link-icon"><item.icon size={18} aria-hidden="true" /></span>
              <span className="dash-link-body">
                <strong>{item.label}</strong>
                <span>{item.text}</span>
              </span>
              <span className="dash-link-go"><ChevronRight size={16} aria-hidden="true" /></span>
            </Link>
          ))}
        </section>
      )}
    </div>
  );
};

const StatusRow = ({ label, value, active }) => (
  <div className={`dash-status-row ${active ? 'is-on' : ''}`}>
    <span className="dash-status-dot" aria-hidden="true" />
    <span className="dash-status-label">{label}</span>
    <span className="dash-status-value">{value}</span>
  </div>
);

const MiniBox = ({ icon: Icon, tone, label, value, title }) => (
  <div className="dash-mini-box" style={{ '--dash-mini-tone': tone }}>
    <span className="dash-mini-icon"><Icon size={16} aria-hidden="true" /></span>
    <span className="dash-mini-body">
      <strong title={title}>{value}</strong>
      <span>{label}</span>
    </span>
  </div>
);

export default Dashboard;
